import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { BackPrintConfig } from "../../../types/props";
import { teams as teamsService, kits as kitsService } from "../../../services/supabase";
import { api } from "../../../services/api";
import { useLoadingMessages } from "../useLoadingMessages";

interface SaveTeamStepProps {
  teamName: string;
  teamSummary: string;
  teamLogoDataUrl: string;
  kitType: string;
  teamwearColor: string;
  backPrint: BackPrintConfig;
  showBackImage: boolean;
  backImageScale: number;
  designName: string;
  previewFrontDataUrl: string;
  previewBackDataUrl: string;
  onBack: () => void;
}

const SaveTeamStep: React.FC<SaveTeamStepProps> = ({
  teamName,
  teamSummary,
  teamLogoDataUrl,
  kitType,
  teamwearColor,
  backPrint,
  showBackImage,
  backImageScale,
  designName,
  previewFrontDataUrl,
  previewBackDataUrl,
  onBack,
}) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { message: saveMsg, animKey: saveKey } = useLoadingMessages(
    ["Saving your team…", "Storing your kit design…", "All done…"],
    saving,
    2000
  );

  const handleSave = async () => {
    if (!user) return;
    setError("");
    setSaving(true);
    try {
      let flagged = false;
      try {
        const [nameCheck, summaryCheck] = await Promise.all([
          api.moderateText(teamName),
          teamSummary ? api.moderateText(teamSummary) : Promise.resolve({ flagged: false }),
        ]);
        flagged = nameCheck.flagged || summaryCheck.flagged;
      } catch (moderationErr) {
        console.error("Failed to moderate team content:", moderationErr);
      }
      if (flagged) {
        throw new Error("Your team name or description can't contain explicit, hateful, or derogatory content. Please go back and edit it.");
      }

      const teamId = crypto.randomUUID();
      const kitId = crypto.randomUUID();

      let logoPublicUrl = "";
      let logoThumbUrl = "";
      if (teamLogoDataUrl) {
        const { data: logoData, error: logoError } = await teamsService.uploadLogoBase64(teamId, teamLogoDataUrl);
        if (logoError) throw logoError;
        logoPublicUrl = logoData!.logoUrl;
        logoThumbUrl = logoData!.thumbUrl;
      }

      const { data: frontUrl, error: frontErr } = await kitsService.uploadPreviewImage(kitId, previewFrontDataUrl, 'front');
      if (frontErr) throw frontErr;
      const { data: backUrl, error: backErr } = await kitsService.uploadPreviewImage(kitId, previewBackDataUrl, 'back');
      if (backErr) throw backErr;

      const { data: teamData, error: teamError } = await teamsService.create({
        team_name: teamName,
        summary: teamSummary,
        logo_url: logoPublicUrl || null,
        logo_thumb_url: logoThumbUrl || null,
        user_email: user.email || "",
        created_by: user.id,
      });
      if (teamError) throw teamError;

      const { error: kitError } = await kitsService.create({
        kit_id: kitId,
        team_id: teamData.id,
        design_name: designName || "Untitled Design",
        created_by_email: user.email || "",
        status: "draft",
        kit_type: kitType,
        teamwear_color: teamwearColor,
        emblem_color: null,
        front_image_url: null,
        front_image_show: true,
        front_image_scale: 100,
        back_image_url: null,
        back_image_show: showBackImage,
        back_image_scale: backImageScale,
        back_print_enabled: backPrint.enabled,
        back_print_text: backPrint.text || null,
        back_print_font: backPrint.font || null,
        back_print_scale: backPrint.scale,
        back_print_position: backPrint.position,
        preview_front_url: frontUrl,
        preview_back_url: backUrl,
        team_name: teamName,
      });
      if (kitError) throw kitError;

      navigate(`/team/${teamData.id}`);
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="bk-checkout">
      <div className="bk-checkout-eyebrow">[ 03 / Save ]</div>
      <h1 className="bk-checkout-title">Ready to go.</h1>
      <p className="bk-checkout-subtitle">Save your team and kit design — you can order it whenever you're ready.</p>

      {error && <div className="bk-error">{error}</div>}

      <div className="bk-checkout-form">
        <div className="bk-checkout-cols">

          {/* LEFT — Kit + team summary */}
          <div className="bk-left">
            {(previewFrontDataUrl || previewBackDataUrl) && (
              <div className="bk-card">
                <div className="bk-card-title">Your Kit — {designName || "Untitled Design"}</div>
                <div className="bk-preview-pair">
                  {previewFrontDataUrl && (
                    <div className="bk-preview-block">
                      <div className="bk-preview-label">Front</div>
                      <img src={previewFrontDataUrl} alt="Kit front" className="bk-preview-img" />
                    </div>
                  )}
                  {previewBackDataUrl && (
                    <div className="bk-preview-block">
                      <div className="bk-preview-label">Back</div>
                      <img src={previewBackDataUrl} alt="Kit back" className="bk-preview-img" />
                    </div>
                  )}
                </div>
                {backPrint.enabled && backPrint.text && (
                  <div className="bk-spec-row">
                    <span className="bk-spec-label">Back print</span>
                    <span className="bk-spec-value">"{backPrint.text}"</span>
                  </div>
                )}
              </div>
            )}

            <div className="bk-card">
              <div className="bk-card-title">Team</div>
              <div className="bk-spec-row">
                <span className="bk-spec-label">Name</span>
                <span className="bk-spec-value">{teamName}</span>
              </div>
              {teamSummary && (
                <div className="bk-spec-row">
                  <span className="bk-spec-label">Pitch</span>
                  <span className="bk-spec-value">{teamSummary}</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Save action */}
          <div className="bk-right">
            <div className="bk-card bk-account-card">
              <div className="bk-card-title">Create Your Team</div>
              <p className="bk-account-desc">
                This saves your team and kit design to your account. You can order it later from your team's page.
              </p>

              <div className="bk-checkout-actions">
                <button type="button" className="bk-btn-text" onClick={onBack} disabled={saving}>
                  ← Back
                </button>
                <button type="button" className="bk-btn" onClick={handleSave} disabled={saving}>
                  {saving ? <span key={saveKey} className="bk-loading-msg">{saveMsg}</span> : "Create Team →"}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SaveTeamStep;
